import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import responseProductDTO = Modules.CampaignPlanner.Data.PriceQuoteApi.responseProductDTO;

/**
 * Whether the flight is bought against an impression goal rather than people (PPL).
 */
export const isImpressionBuy = (flight: FlightDO | null | undefined): boolean =>
	flight?.type_of_buy === "goal_impressions";

/**
 * Sum of the bookable impressions across a set of products. This is the
 * denominator when distributing an impression goal over a segment — it matches
 * the `max_available_impressions` numerator in `productContacts` so the
 * delivered contacts sum up to the goal (capped by capacity).
 */
export const sumMaxAvailableImpressions = (
	products: responseProductDTO[]
): number =>
	products.reduce(
		(sum, p) => sum + (p.used_availability?.max_available_impressions ?? 0),
		0
	);

/**
 * Fraction of a segment's bookable impressions that is actually bought to hit
 * the flight's impression goal, capped at 1 (you never buy more than 100%).
 * This is the "share" that distributes both contacts and revenue across the
 * products of a goal_impressions segment.
 */
export const impressionGoalShare = (
	flight: FlightDO | null | undefined,
	segmentMaxAvailableImpressions: number
): number =>
	segmentMaxAvailableImpressions > 0
		? Math.min((flight?.goal_target ?? 0) / segmentMaxAvailableImpressions, 1)
		: 0;

/**
 * The contact metric for a single product.
 * - PPL flights measure reach directly via `pqa_audience`.
 * - goal_impressions flights measure delivered impressions, capped by the
 *   product's SOV and the flight's impression goal (distributed across the
 *   segment by each product's share of the total available impressions).
 *
 * `segmentAudienceTotalImpressions` is passed in (rather than derived here) so
 * the caller can reuse a total it has already computed for the whole segment.
 */
export const productContacts = (
	flight: FlightDO | null | undefined,
	product: responseProductDTO,
	segmentMaxAvailableImpressions: number
): number => {
	if (!isImpressionBuy(flight)) {
		return product.pqa_audience ?? 0;
	}

	const availability = product.used_availability;
	const goalShare = impressionGoalShare(flight, segmentMaxAvailableImpressions);

	return (
		Math.min(
			(product.maximum_sov_used ?? 0) *
				(availability?.audience_total_impression ?? 0),
			availability?.max_available_impressions ?? 0
		) * goalShare
	);
};

/**
 * The maximum contacts a product can deliver (the goal_impressions contact
 * metric at full share), i.e. its SOV-capped available impressions. This is the
 * "Max. available" figure and the denominator for a product's delivered share.
 */
export const productMaxContacts = (product: responseProductDTO): number => {
	const availability = product.used_availability;

	return Math.min(
		(product.maximum_sov_used ?? 0) *
			(availability?.audience_total_impression ?? 0),
		availability?.max_available_impressions ?? 0
	);
};

/**
 * The CPM for a single product. Trusted as a per-screen constant supplied by
 * the price quote — identical for PPL and goal_impressions, so callers never
 * need to branch on `type_of_buy` to read it.
 */
export const productCpm = (product: responseProductDTO): number =>
	product.pqa_cpm_average ?? 0;

/**
 * The revenue metric for a single product.
 * - PPL flights bill the full quoted price (`pqa_pricequote_exvat`) directly —
 *   nothing to compute.
 * - goal_impressions flights derive price from delivered contacts and the
 *   product's CPM (`contacts / 1000 × cpm`), rather than scaling the quoted
 *   price — this keeps price in lockstep with contacts without depending on
 *   `pqa_pricequote_exvat` at all for this buy type.
 */
export const productRevenue = (
	flight: FlightDO | null | undefined,
	product: responseProductDTO,
	segmentMaxAvailableImpressions: number
): number => {
	if (!isImpressionBuy(flight)) {
		return product.pqa_pricequote_exvat ?? 0;
	}

	const contacts = productContacts(
		flight,
		product,
		segmentMaxAvailableImpressions
	);

	return (contacts / 1000) * productCpm(product);
};
